import * as React from 'react';
import { DatePicker, Form } from 'antd';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import "moment-timezone";
const FormItem = Form.Item;

import { TextView } from './text.view';

export enum DisableKind {
	disableSeconds = 1,
	disableMinutes = 2,
	disableHours = 4,
}

interface DateViewProps {
	mode: ControlMode;
	format: ControlDateFormat;
	label?: string;
	required?: boolean;
	value?: Date;
	disabled?: boolean;
	disabledDate?: (d: Date) => boolean;
	onChanged?: (newValue: Date) => void;
	onGetErrorMessage?: () => Promise<ValidationError[]>;
	showTime?: boolean | DisableKind;
	addonBefore?: React.ReactNode;
	addonAfter?: React.ReactNode;
	dateUsage?: "utc" | "tz";
	readOnly?: boolean;
	style?: React.CSSProperties;
	withoutFormWrapper?: boolean;
}

interface DateViewState {
	value: moment.Moment;
	errors: ValidationError[];
}

@observer
export class DateView extends React.Component<DateViewProps, DateViewState> {
	constructor(props: Readonly<DateViewProps>) {
		super(props);

		this.state = {
			errors: [],
			value: this.parseDate(props.value)
		};
	}

	public componentWillReceiveProps(nextProps: Readonly<DateViewProps>): void {
		if (nextProps.value != this.props.value) {
			this.setState({
				value: this.parseDate(nextProps.value)
			});
		}
	}

	public render() {
		const {
			addonAfter,
			addonBefore,
			disabled,
			disabledDate,
			label,
			mode,
			required,
			showTime,
			style,
			withoutFormWrapper,
		} = this.props;

		let dateFormat = 'DD/MM/YYYY';
		let format = '';
		if (showTime) {
			if (showTime === true) {
				dateFormat += ' HH:MM';
			} else {
				if ((showTime & DisableKind.disableHours) != DisableKind.disableHours) {
					format += ' HH';
				}
				if ((showTime & DisableKind.disableMinutes) != DisableKind.disableMinutes) {
					format += (format == '' ? '' : ':') + 'mm';
				}
				if ((showTime & DisableKind.disableSeconds) != DisableKind.disableSeconds) {
					format += (format == '' ? '' : ':') + 'ss';
				}
				dateFormat += format;
			}
		}

		let value = this.state.value;

		if (this.props.readOnly) {
			return (
				<FormItem
					label={label}
				>
					<p><strong>{value ? value.format(dateFormat) : ''}</strong></p>
				</FormItem>)
		}

		if (mode === 'display') {
			return (
				<TextView
					mode={mode}
					label={label}
					value={value ? value.format(dateFormat) : undefined}
					addonAfter={addonAfter}
					addonBefore={addonBefore}
					style={style}
				/>
			);
		}
		const datePicker = (
			<DatePicker
				allowClear={!required}
				disabled={disabled}
				showToday={true}
				placeholder="Select Date"
				value={value}
				disabledDate={this.onDisabledDate}
				format={dateFormat}
				onChange={this.onSelectDate}
				showTime={typeof showTime === 'boolean'
					? showTime
					: showTime == null
						? undefined
						: { format: format }
				}
				style={style && style.width ? style : style ? { ...style, width: "100%" } : { width: "100%" }}
			/>
		);
		if (withoutFormWrapper) {
			return (
				<React.Fragment>
					{(addonBefore || addonAfter) && this.renderAddon(datePicker)}
					{!addonBefore && !addonAfter && datePicker}
				</React.Fragment>
			);
		}
		return (
			<FormItem
				label={label}
				required={required}
				validateStatus={this.state.errors.length == 0 ? 'success' : 'error'}
			>
				{(addonBefore || addonAfter) && this.renderAddon(datePicker)}
				{!addonBefore && !addonAfter && datePicker}
			</FormItem>
		);
	}

	private renderAddon(datePicker: React.ReactNode) {
		const { addonAfter, addonBefore } = this.props;

		return (
			<span className="ant-input-group-wrapper">
				<span className="ant-input-wrapper ant-input-group">
					{addonBefore && <span className="ant-input-group-addon">{addonBefore}</span>}
					<span className="ant-input-affix-wrapper ant-input-affix-wrapper-date">
						{datePicker}
					</span>
					{addonAfter && <span className="ant-input-group-addon">{addonAfter}</span>}
				</span>
			</span>
		);
	}

	private onSelectDate = async (newDate: moment.Moment) => {
		if (newDate) {
			newDate = this.parseDate(newDate.format().substr(0, 19));
		}
		if (!this.props.showTime && newDate != null) {
			newDate = newDate.startOf('day');
		}

		if (this.props.required && newDate == null) {
			return;
		}

		this.props.onChanged(newDate ? newDate.toDate() : null);

		if (this.props.onGetErrorMessage != null) {
			const errors = await this.props.onGetErrorMessage();
			this.setState({
				errors,
				value: newDate
			});
		} else {
			this.setState({
				errors: [],
				value: newDate
			});
		}
	}

	private onDisabledDate = (date: moment.Moment) => {
		if (this.props.disabledDate) {
			return date && this.props.disabledDate(date.toDate());
		}

		return false;
	}

	private parseDate(value?: any): moment.Moment {
		return value
			? this.props.dateUsage
				? this.props.dateUsage === "tz"
					? moment.tz(value, "Europe/London")
					: moment.utc(value)
				: moment(value)
			: undefined;
	}
}



// WEBPACK FOOTER //
// ./src/core/ui/date.view.tsx